import useTranslation from 'next-translate/useTranslation'
import { Dispatch, SetStateAction } from 'react'
import CurrencyInput from 'react-currency-input-field'
import MaskedInput from 'react-maskedinput'
import NumberFormat from 'react-number-format'
import { FormikErrors } from 'formik'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import stylesTooltip from '@/styles/Tooltip.module.scss'
import stylesInput from '@/styles/Input.module.scss'

export type Errors<FormType> = {
            [K in keyof FormType]?: string;
}

type InputType<T> = T | string | number

type Props<T, FormType> = {
  withDiv?: boolean,
  type?: 'percentage' | 'carddate' | 'email' | 'mask' | 'cvv3' | 'textarea' | 'text' | 'currency' | 'number' | 'phone' | 'date' | 'card' | 'search',
  defaultValue?: string | number,
  onChange?: (name: string, value: InputType<T>) => void,
  onSet?: ((value: T) => void) | Dispatch<SetStateAction<T>>,
  name?: string,
  form?: FormType,
  errors?: FormikErrors<FormType> | Errors<FormType>,
  value?: string | number,
  error?: string,
  className?: string,
  labelKey?: string,
  label?: string,
  labelClass?: string,
  onlyInput?: boolean,
  required?: boolean,
  readOnly?: boolean,
  inputProps?: Record<string, any>,
  eClass?: string,
  disabled?: boolean,
  val?: T,
  currency?: string,
  noFieldSet?: boolean,
  inputClass?: string,
  id?: string,
  style?: React.CSSProperties,
  children?: React.ReactNode | React.ReactNode[],
  eStyle?: React.CSSProperties,
  placeholder?: string,
  autoComplete?: string,
  onSubmit?: () => void,
  rows?: number,
  maxLength?: number,
  decimalsLimit?: number,
  isAllowed?: any,
            allowNegativeValue ?: boolean,
            alwaysPositive ?: boolean
}

const GInput = <T, FormType extends Record<string, any>>({
  withDiv,
  type,
  defaultValue,
  onChange,
  onSet,
  name,
  form,
  errors,
  value,
  error,
  className,
  labelKey,
  label,
  labelClass,
  onlyInput,
  required,
  readOnly,
  inputProps,
  eClass,
  children,
  val,
  currency,
  id,
  onSubmit,
  eStyle,
  noFieldSet,
  inputClass,
  disabled,
  alwaysPositive,
  ...rest
}: Props<T, FormType>) => {
  const { t } = useTranslation()
  const onFormChange = (event) => {
    if (!disabled) {
      let value = null
      if (type === 'percentage') {
        value = alwaysPositive ? Math.abs(event.floatValue) : event.floatValue
        if (!value) {
          value = 0
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
      } else if (type === 'number' || type === 'currency' || type === 'percentage') {
        // event values{floatValue formattedValue value}
        if (!event.floatValue || `${event.floatValue}` === event.value) {
          value = alwaysPositive ? Math.abs(event.floatValue) : event.floatValue
        } // event=moment date
      } else {
        value = event.target.value
      }

      //  if (!value && value !== 0 && (type === 'number' || type === 'currency' || type === 'percentage')) return;
      if (form && onChange && name) {
        onChange(name, value)
      } else if (onSet) {
        onSet(value)
      } else {
        throw Error('onChange or onSet should be defined for input')
      }
    }
  }

  const getError = (): string => {
    if (errors && errors[name]) {
      return typeof errors[name] === 'string' && (errors[name] as string).includes('common:') ? t(errors[name] as string) : errors[name] as string
    }
    return error
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && onSubmit) {
      onSubmit()
    }
  }

  const handleCurrency = (floatvalue) => {
    if (form && onChange && name) {
      onChange(name, alwaysPositive ? floatvalue : floatvalue)
    } else if (onSet) {
      onSet(alwaysPositive ? floatvalue : floatvalue)
    }
  }

  const errorText = getError()
  const renderOnlyInput = () => {
    const value = form && form[name] ? form[name] : val || ''

    switch (type) {
      case 'number':
        return (
          <NumberFormat
            placeholder={rest.placeholder || '0,00'}
            className={`${className || 'form-input w-100'}${getError() ? ' error' : ''}`}
            onValueChange={onFormChange}
            value={value}
            disabled={readOnly}
            name={name}
            thousandSeparator=","
            decimalSeparator="."
            {...rest}
          />
        )
      case 'currency':
        return (
          <CurrencyInput
            intlConfig={{ locale: 'en-US', currency: 'EUR' }}
            prefix={currency || '€'}
            disabled={readOnly}
            className={`${className || 'form-input w-100'}${getError() ? ' error' : ''}`}
            value={value}
            name={name}
            onKeyDown={handleKeyDown}
            onValueChange={(value) => handleCurrency(value)}
            {...rest}
          />
        )
      case 'percentage':
        return (
          <NumberFormat
            placeholder="% 0"
            className={`${className || 'form-input w-100'}${getError() ? ' error' : ''}`}
            onValueChange={onFormChange}
            prefix=" %"
            disabled={readOnly}
            value={value}
            {...rest}
          />
        )
      // Not being used
      case 'phone':
        return (
          <MaskedInput
            className={`${className || 'form-input w-100'}${getError() ? ' error' : ''}`}
            onChange={onFormChange}
            mask="+90 (###) ###-####"
            value={value}
          />
        )
      // Not being used
      case 'date':
        return (
          <MaskedInput
            className={`${className || 'form-input w-100'}${getError() ? ' error' : ''}`}
            onChange={onFormChange}
            mask="11.11.1111"
            placeholder="DD/MM/YYYY"
            disabled={readOnly}
            value={value}
          />
        )
      // Not being used
      case 'card':
        return (
          <MaskedInput
            className={`${className || 'form-input w-100'}${getError() ? ' error' : ''}`}
            onChange={onFormChange}
            mask="####-####-####-####"
            placeholder=""
            value={value}
          />
        )
      // Not being used
      case 'carddate':
        return (
          <MaskedInput
            className={`${className || 'form-input w-100'}${getError() ? ' error' : ''}`}
            onChange={onFormChange}
            mask="##/##"
            placeholder="AA/YY"
            value={value}
          />
        )
      // Not being used
      case 'cvv3':
        return (
          <MaskedInput
            className={`${className || 'form-input w-100'}${getError() ? ' error' : ''}`}
            onChange={onFormChange}
            mask="111"
            value={value}
            {...rest}
          />
        )

      // Not being used
      case 'mask':
        return (
          <MaskedInput
            name={name}
            className={`${className || 'form-input w-100'}${getError() ? ' error' : ''}`}
            onChange={onFormChange}
            value={value}
            {...rest}
          />
        )
      case 'textarea':
        return (
          <textarea
            id={id}
            name={name}
            className={className || 'form-textarea'}
            value={value}
            onChange={onFormChange}
            {...rest}
          >
            {value}
          </textarea>
        )
      case 'search':
        return (
          <div className="tennders-search">
            <input
              onKeyDown={handleKeyDown}
              autoComplete={`new-${name || 'password'}`}
              id={id}
              name={name}
              placeholder={t('searchFilters:searchDirectory')}
              disabled={!!disabled}
              required={!!required}
              readOnly={!!readOnly}
              className={`${className || 'form-input w-100'}${getError() ? ' error ' : ''}`}
              value={value || (form && form[name]) || defaultValue || ''}
              type="text"
              onChange={onFormChange}
              {...rest}
            />
            <button type="button">
              <FontAwesomeIcon icon={faSearch} aria-hidden="true" />
            </button>
          </div>
        )
      default:
        return (
          <input
            onKeyDown={handleKeyDown}
            autoComplete={`new-${name || 'password'}`}
            id={id}
            name={name}
            disabled={!!disabled}
            required={!!required}
            readOnly={!!readOnly}
            className={`${className || 'form-input w-100'}${getError() ? ' error ' : ''}`}
            value={value || (form && form[name]) || defaultValue || ''}
            type={type || 'text'}
            onChange={onFormChange}
            {...rest}
          />
        )
    }
  }

  const showResults = () => {
    if (onlyInput) {
      return (
        <div>
          {renderOnlyInput()}
          {errorText && errorText !== ''
            ? (
              <span className="mt-10 error-text">
                <span className={stylesTooltip['tooltip-error']} data-tooltip={getError()}>
                  <img src="/img/red-question-mark-in-circle.png" alt="" />
                </span>
              </span>
            ) : null}
        </div>
      )
    }
    if (withDiv) {
      return (
        <div {...rest} className={eClass || 'mr-8'}>
          <label
            htmlFor="input-id"
            className={labelClass || 'form-label'}
          >
            {' '}
            {labelKey ? t(labelKey) : label}
          </label>
          {renderOnlyInput()}
          {errorText && errorText !== ''
            ? (
              <span className="mt-10 error-text">
                <span className={stylesTooltip['tooltip-error']} data-tooltip={getError()}>
                  <img src="/img/red-question-mark-in-circle.png" alt="" />
                </span>
              </span>
            ) : null}
        </div>
      )
    }
    if (noFieldSet) {
      return (
        <>
          <label
            htmlFor="input-id-2"
            className={labelClass || 'form-label'}
          >
            {' '}
            {labelKey ? t(labelKey) : label}
          </label>
          {renderOnlyInput()}
          {errorText && errorText !== ''
            ? (
              <span className="mt-10 error-text">
                <span className={stylesTooltip['tooltip-error']} data-tooltip={getError()}>
                  <img src="/img/red-question-mark-in-circle.png" alt="" />
                </span>
              </span>
            ) : null}
        </>
      )
    }
    return (
      <>
        <fieldset className={eClass || 'mb-2'}>
          <>
            <label
              htmlFor="input-id-3"
              className={(labelClass || 'form-label d-flex justify-start')}
            >
              {' '}
              {labelKey ? t(labelKey) : label}
              {required && <span className={stylesInput.required}>*</span>}
            </label>
            {renderOnlyInput()}
            {errorText && errorText !== ''
              ? (
                <span className="mt-10 error-text">
                  <span className={stylesTooltip['tooltip-error']} data-tooltip={getError()}>
                    <img src="/img/red-question-mark-in-circle.png" alt="" />
                  </span>
                </span>
              ) : null}
          </>
        </fieldset>
      </>
    )
  }

  return showResults()
}

export default GInput
